/*
 * The three statuses the compliance verdict API recognises. `new` is a failed factory check nobody
 * has ruled on yet; granting an override on it makes it `active`. There is no delete and no
 * reinstate: an override is withdrawn by revoking it, which keeps it readable as audit history.
 *
 * Both transitions are one-way here, unlike firmware advisories, whose active/revoked enum is freely
 * flippable in both directions. Do not infer one from the other.
 */
export const COMPLIANCE_VERDICT_STATUS = {
	NEW: 'new',
	ACTIVE: 'active',
	REVOKED: 'revoked'
};

export const COMPLIANCE_VERDICT_STATUS_LABELS = {
	[COMPLIANCE_VERDICT_STATUS.NEW]: 'New',
	[COMPLIANCE_VERDICT_STATUS.ACTIVE]: 'Active',
	[COMPLIANCE_VERDICT_STATUS.REVOKED]: 'Revoked'
};

/*
 * `All` is the empty string rather than a sentinel word because `stripBlankParams` drops blank
 * values before the query string is built, and omitting `status` is exactly how the API says
 * "every status".
 */
export const COMPLIANCE_VERDICT_STATUS_FILTERS = [
	{ value: '', label: 'All' },
	{ value: COMPLIANCE_VERDICT_STATUS.NEW, label: COMPLIANCE_VERDICT_STATUS_LABELS[COMPLIANCE_VERDICT_STATUS.NEW] },
	{ value: COMPLIANCE_VERDICT_STATUS.ACTIVE, label: COMPLIANCE_VERDICT_STATUS_LABELS[COMPLIANCE_VERDICT_STATUS.ACTIVE] },
	{ value: COMPLIANCE_VERDICT_STATUS.REVOKED, label: COMPLIANCE_VERDICT_STATUS_LABELS[COMPLIANCE_VERDICT_STATUS.REVOKED] }
];

/*
 * compass-react `Badge` takes `appearance`, not `role` - a Badge given `role` renders unstyled.
 *
 * Deliberately a separate map from ADVISORY_STATUS_APPEARANCE rather than an import: the two
 * features share two status names by coincidence, not by contract.
 *
 * `new` is a warning because it is a failed check awaiting a decision - the one status on this page
 * that asks the operator to do something.
 */
export const COMPLIANCE_VERDICT_STATUS_APPEARANCE = {
	[COMPLIANCE_VERDICT_STATUS.NEW]: 'warning',
	[COMPLIANCE_VERDICT_STATUS.ACTIVE]: 'success',
	[COMPLIANCE_VERDICT_STATUS.REVOKED]: 'secondary'
};

export const DEFAULT_APPEARANCE = 'secondary';

/*
 * The three granularities the API supports, expressed as one choice rather than as two optional
 * fields. The fourth combination the fields could express - a firmware version with no component
 * type - is rejected by the API with a 422, so the form never offers it.
 */
export const OVERRIDE_SCOPES = {
	APPLIANCE: 'appliance',
	COMPONENT_TYPE: 'componentType',
	COMPONENT_VERSION: 'componentVersion'
};

/*
 * Ordered from the expected case to the rare one, not widest-first. A waiver is expected to cover one
 * component type - the QA gate fails on a specific component whose firmware cannot be updated (spec
 * §10) - while an appliance-wide waiver exempts every component on the unit and should be the
 * deliberate exception, so it comes last.
 */
export const OVERRIDE_SCOPE_OPTIONS = [
	{
		value: OVERRIDE_SCOPES.COMPONENT_TYPE,
		label: 'One component type',
		description: 'Every component of the chosen type is exempt, on any firmware version.'
	},
	{
		value: OVERRIDE_SCOPES.COMPONENT_VERSION,
		label: 'One component type on one firmware version',
		description: 'Only components of the chosen type running that exact version are exempt.'
	},
	{
		value: OVERRIDE_SCOPES.APPLIANCE,
		label: 'Whole appliance',
		description: 'Every component on this appliance is exempt, on any firmware version.'
	}
];

export const OVERRIDE_SCOPE_LABELS = {
	[OVERRIDE_SCOPES.APPLIANCE]: 'Whole appliance',
	[OVERRIDE_SCOPES.COMPONENT_TYPE]: 'Component type',
	[OVERRIDE_SCOPES.COMPONENT_VERSION]: 'Component type and version'
};

/*
 * A blank `serialNumber` lists every appliance: `stripBlankParams` drops it, and the API treats an
 * absent serial as "all". The status defaults to every status so that granting a `new` row leaves
 * it on screen, now reading Active, instead of filtering it away.
 */
export const GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS = {
	start: 0,
	limit: 15,
	serialNumber: '',
	status: ''
};

/*
 * The verdict list endpoint caps `limit` at 200 like the rest of the hardware API, so the page size
 * list must not offer more.
 */
export const COMPLIANCE_VERDICTS_SIZE_PER_PAGE_LIST = [15, 30, 60, 100, 200];

export const CREATE_FORM_NAME = 'complianceVerdictCreate';

export const GRANT_FORM_NAME = 'complianceVerdictGrant';

/*
 * Create, grant and revoke all return a job id rather than the verdict, so every write is followed
 * by a poll. Sixty seconds is the same budget `pollJobUntilTerminal` defaults to; it is named here
 * so the form and the two dialogs cannot drift apart.
 */
export const OVERRIDE_JOB_POLL = {
	intervalMs: 1000,
	maxAttempts: 60
};

/*
 * Opens on "One component type": it is the common case, and an operator who never touches the
 * scope control should end up with the narrow waiver they most likely meant, not the widest one.
 */
export const EMPTY_OVERRIDE_FORM_VALUES = {
	serialNumber: '',
	scope: OVERRIDE_SCOPES.COMPONENT_TYPE,
	hardwareComponentTypeId: '',
	firmwareVersion: '',
	reason: ''
};
